import { setAnalyticsUser, clearAnalyticsUser } from "./analytics";

const ACCESS_KEY = "ym_access";
const REFRESH_KEY = "ym_refresh";
const USER_KEY = "ym_user";
const DEVICE_KEY = "ym_device_id";

export type AuthUser = {
  id: number;
  username?: string;
  email?: string | null;
  msisdn?: string | null;
  phone_number?: string | null;
  mobile_number?: string | null;
  name?: string | null;
  role?: string;
  is_guest?: boolean;
  is_premium?: boolean;
  /** ATOM/Yatha identity marker only; it does not represent a paid subscription. */
  isAtomYathaUser?: boolean;
  is_new_user?: boolean;
  auth_provider?: string | null;
  provider?: string | null;
  login_provider?: string | null;
  profile_picture?: string | null;
  image?: string | null;
};

function legacyValue(key: string) {
  return localStorage.getItem(key);
}

export const auth = {
  access: () => localStorage.getItem(ACCESS_KEY) || legacyValue("authToken"),
  refresh: () => localStorage.getItem(REFRESH_KEY) || legacyValue("refreshToken"),
  user: (): AuthUser | null => {
    const stored = localStorage.getItem(USER_KEY) || legacyValue("user");
    try {
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },
  deviceId() {
    let deviceId = localStorage.getItem(DEVICE_KEY);
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem(DEVICE_KEY, deviceId);
    }
    return deviceId;
  },
  save(data: { access: string; refresh: string; user?: AuthUser | unknown }) {
    const previousUser = auth.user();
    localStorage.setItem(ACCESS_KEY, data.access);
    localStorage.setItem(REFRESH_KEY, data.refresh);
    if (data.user) {
      const u = data.user as any;
      if (previousUser?.id != null && u.id != null && String(previousUser.id) !== String(u.id)) {
        localStorage.removeItem("subscriptionStatus");
        localStorage.removeItem("subscriptions");
      }
      const isPrem = Boolean(
        u.is_premium ||
        u.role === "premium" ||
        u.role === "vip" ||
        u.is_subscribed ||
        u.has_subscription ||
        u.has_active_subscription ||
        u.is_vip
      );
      const normalizedUser = {
        ...u,
        ...(isPrem ? { is_premium: true } : {}),
      };
      localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
      try {
        localStorage.setItem("user_status", JSON.stringify({
          ...u,
          is_premium: isPrem,
        }));
      } catch {}
      setAnalyticsUser(normalizedUser);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("ym_auth_changed", { detail: normalizedUser }));
        window.dispatchEvent(new CustomEvent("ym_quota_updated"));
      }
    }
  },
  saveTokens(access: string, refresh?: string) {
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  saveUser(user: AuthUser) {
    const u = user as any;
    const isPrem = Boolean(
      u.is_premium ||
      u.role === "premium" ||
      u.role === "vip" ||
      u.is_subscribed ||
      u.has_subscription ||
      u.has_active_subscription ||
      u.is_vip
    );
    const normalizedUser = {
      ...u,
      ...(isPrem ? { is_premium: true } : {}),
    };
    localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
    try {
      localStorage.setItem("user_status", JSON.stringify({
        ...u,
        is_premium: isPrem,
      }));
    } catch {}
    setAnalyticsUser(normalizedUser);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ym_auth_changed", { detail: normalizedUser }));
      window.dispatchEvent(new CustomEvent("ym_quota_updated"));
    }
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("user_status");
    localStorage.removeItem("subscriptionStatus");
    clearAnalyticsUser();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ym_auth_changed", { detail: null }));
      window.dispatchEvent(new CustomEvent("ym_quota_updated"));
    }
  },
};
